// @ts-check
import React from 'react';
import { Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  setFilterGroup,
  setNamedAuthorFilterGroup,
} from 'actions/organisation';
import { Token } from 'components/common/TokenInput';
import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import { searchOrigin } from 'components/Search/Results/constants';
import { FiltersContainer } from 'components/Search/Results/styled';
import { getFilters } from 'selectors/organisation';
import { orgPageExcludedFilters, tokenise } from 'utils/filters';

// The org-page counterpart of Search/Results/SearchFilters: the same removable
// chips in the same place, under the "Search Results" heading, reading the shared
// org `filters` slice instead of the `search` one. Like main search it only
// dispatches the filter change — the results and the stat tiles refresh on the
// next Search, so a chip removal cannot leave the figures beside it describing a
// query nobody ran.

// Keys with their own control in the search area above. A chip for these would
// be a second, competing way to change one control — main search leaves them out
// of its own token row for the same reason. `workItemCategories` ("Search
// across") is already covered by orgPageExcludedFilters.
const ownControlOnPage = ['term', 'date', 'latestFundingRoundsOnly'];

const TokenLister = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  > span {
    font-size: 13px;
    line-height: 13px;
  }
`;

const TooltipTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

/**
 * @param {{ organisationId: number }} props
 */
const OrgSearchFilterTokens = ({ organisationId }) => {
  const dispatch = useDispatch();
  const filters = useSelector(getFilters(organisationId));

  const tokens = tokenise(
    filters ?? {},
    GetSearchKeysByView(searchOrigin.organisationPageSearch),
    [...orgPageExcludedFilters, ...ownControlOnPage]
  );

  /**
   * @param {string} key
   * @param {number=} index
   * @param {string=} filterGroup
   */
  const removeFilterValue = (key, index, filterGroup) => {
    // namedAuthor is the only nested group that reaches this row; its members
    // are booleans, so they are always removed whole rather than by index.
    const setter =
      filterGroup === 'namedAuthor'
        ? setNamedAuthorFilterGroup
        : setFilterGroup;

    if (index == null) {
      dispatch(setter(organisationId, key, undefined));
      return;
    }

    dispatch(
      setter(
        organisationId,
        key,
        filters[key].filter((_, i) => i !== index)
      )
    );
  };

  if (tokens.length <= 0) return null;

  return (
    <FiltersContainer data-testid='org-search-filter-tokens'>
      <TokenLister>
        <span style={{ marginRight: '10px' }}>Search Filters:</span>
        {tokens.map(filter => (
          /* @ts-ignore */
          <Tooltip
            key={`${filter.key}-${filter.index}`}
            title={
              <TooltipTitle>
                <div style={{ fontWeight: 'bold' }}>
                  {filter.metaData.sectionTitle}
                </div>
                <div>{filter.metaData.title}</div>
              </TooltipTitle>
            }
          >
            {/* @ts-ignore */}
            <Token
              style={{
                margin: 0,
                marginRight: '5px',
                marginTop: '5px',
                marginBottom: '5px',
              }}
              value={filter.value}
              removeToken={() =>
                removeFilterValue(filter.key, filter.index, filter.filterGroup)
              }
            />
          </Tooltip>
        ))}
      </TokenLister>
    </FiltersContainer>
  );
};

export default OrgSearchFilterTokens;
